import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { useSession } from "../auth";
import { useProductConfig } from "../productConfig";

/**
 * Google Identity Services, rendered into our own page.
 *
 * The whole reason this screen is three dozen lines instead of an
 * `apply_branding.sh` archaeology dig: it is our HTML. The `hd` hint below
 * only prefills the account chooser — the API verifies the domain server-side
 * on the ID token, which is the check that counts.
 */

interface GoogleAccounts {
  accounts: {
    id: {
      initialize(config: {
        client_id: string;
        callback: (response: { credential: string }) => void;
        hd?: string;
        auto_select?: boolean;
      }): void;
      renderButton(
        parent: HTMLElement,
        options: Record<string, string | number>,
      ): void;
    };
  };
}

declare global {
  interface Window {
    google?: GoogleAccounts;
  }
}

const API: string = import.meta.env["VITE_API_URL"] ?? "http://localhost:8080";
let initializedGoogleIdentity: string | null = null;
let googleCredentialHandler: ((credential: string) => void) | null = null;

export function SignIn(): ReactNode {
  const slot = useRef<HTMLDivElement | null>(null);
  const { signIn, retrySession, error } = useSession();
  const {
    config,
    configurationStatus,
    configurationError,
    retryConfiguration,
  } = useProductConfig();
  const clientId = config.auth.googleClientId;
  const clientIsReal =
    clientId.endsWith(".apps.googleusercontent.com") &&
    !clientId.startsWith("replace-me") &&
    !clientId.startsWith("fixture-") &&
    !clientId.startsWith("local-development-only");

  useEffect(() => {
    const handler = (credential: string): void => void signIn(credential);
    googleCredentialHandler = handler;
    return () => {
      if (googleCredentialHandler === handler) googleCredentialHandler = null;
    };
  }, [signIn]);

  useEffect(() => {
    if (!clientIsReal) return;

    // The GSI script is loaded `async defer`, so it may not be there yet.
    const attach = (): boolean => {
      const google = window.google;
      const parent = slot.current;
      if (!google || !parent) return false;

      const identityKey = `${clientId}\n${config.auth.hostedDomain}`;
      if (initializedGoogleIdentity !== identityKey) {
        google.accounts.id.initialize({
          client_id: clientId,
          hd: config.auth.hostedDomain,
          callback: (response) =>
            googleCredentialHandler?.(response.credential),
        });
        initializedGoogleIdentity = identityKey;
      }
      parent.replaceChildren();
      google.accounts.id.renderButton(parent, {
        theme: "filled_black",
        size: "large",
        shape: "pill",
        text: "signin_with",
        width: 260,
      });
      return true;
    };

    if (attach()) return;
    const poll = setInterval(() => {
      if (attach()) clearInterval(poll);
    }, 120);
    return () => clearInterval(poll);
  }, [clientId, clientIsReal, config.auth.hostedDomain]);

  return (
    <div
      className="signin"
      style={
        {
          // `none` rather than a hard-coded file: the gradient beneath this
          // layer stands on its own, and a deployment that has not supplied
          // its own artwork must not be handed ours.
          "--signin-image":
            config.product.signInBackground === undefined
              ? "none"
              : `url(${config.product.signInBackground})`,
        } as CSSProperties
      }
    >
      <div className="signin__panel">
        <img src={config.product.lockup} alt={config.product.name} />
        <p className="signin__body">
          {config.product.tagline} Sign in with your @{config.auth.hostedDomain}{" "}
          account.
        </p>
        <div className="signin__slot" ref={slot}>
          {clientIsReal ? null : <DevSignIn />}
        </div>
        {configurationStatus === "failed" ? (
          <div className="signin__recovery" role="alert">
            <p className="signin__error">
              Could not load sign-in settings.
              {clientIsReal
                ? " You can still sign in."
                : " Sign-in will return when Neulytics reconnects."}
            </p>
            {configurationError === null ? null : (
              <p className="signin__note">
                Sign-in settings are temporarily unavailable.
              </p>
            )}
            <button
              type="button"
              className="button"
              onClick={() => void retryConfiguration()}
            >
              Try again
            </button>
          </div>
        ) : null}
        {error ? (
          <div className="signin__recovery" role="alert">
            <p className="signin__error">{error}</p>
            <button className="button" onClick={retrySession}>
              Try again
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The local-development door, shown only while no real OAuth client exists.
 *
 * The server decides whether this works: the endpoint is not registered unless
 * NODE_ENV is not production AND the session secret is the documented local
 * value, so this button 404s everywhere it should. Rendering it is a
 * convenience; refusing it is the control.
 */
function DevSignIn(): ReactNode {
  const [state, setState] = useState<"idle" | "working" | "failed">("idle");

  const enter = async (): Promise<void> => {
    setState("working");
    try {
      const response = await fetch(`${API}/v1/auth/dev`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!response.ok) throw new Error(String(response.status));
      // A full reload rather than a state update: identity is read once on
      // mount, and this is the same path a real sign-in takes afterwards.
      window.location.reload();
    } catch {
      setState("failed");
    }
  };

  return (
    <>
      <button
        type="button"
        className="button"
        onClick={() => void enter()}
        disabled={state === "working"}
      >
        {state === "working" ? "Signing in…" : "Continue as developer"}
      </button>
      <p className="signin__note">
        Local development only. This opens a demo session without Google sign-in
        and is unavailable in production.
      </p>
      {state === "failed" ? (
        <span className="signin__error">
          Local developer sign-in is disabled. Check SESSION_SECRET in the
          server settings.
        </span>
      ) : null}
    </>
  );
}
